/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { formatDateForInputWithOffset } from '../../common/UtilityFunctions';
import Styles from '../../css/LoanInformation.module.css';
import EnvelopeWithBox from '../EnvelopeWithBox';
import { useLoanInformationContext } from '../../contexts/LoanInformationContext';
import { useCallback, useState } from 'react';

const ThirdPartyInfo = () => {
  const { loanContacts, handleLoanContactChange, contactsQID } = useLoanInformationContext();

  const [selectedContact, setSelectedContact] = useState('');
  const contact = loanContacts[selectedContact];
  const activeContacts = (contactsQID ?? []).filter((contactQID) => loanContacts?.[contactQID.code]?.active);

  const handleContactChange = useCallback(
    (code: string, variable: string, val: any) => {
      handleLoanContactChange('edit', code + '.' + variable, val);
    },
    [handleLoanContactChange],
  );
  return (
    <EnvelopeWithBox blockName='Authorized Third Party List' className={Styles.thirdPartyEnvelope}>
      <div className={Styles.loanInfoTable}>
        <div className={`${Styles.leftColumn} ${Styles.thirdPartyColumn}`}>
          <table className={`${Styles.loanInfoTableSection} ${Styles.thirdPartyTable}`}>
            <tbody>
              {activeContacts.length > 0 && (
                <tr>
                  <th>Active authorized third party:</th>
                  <td>
                    <div className={Styles.activeThirdPartyList}>
                      {activeContacts.map((activeContact) => (
                        <button
                          key={activeContact.code}
                          type='button'
                          className={`${Styles.activeThirdPartyLink} ${
                            selectedContact === activeContact.code ? Styles.activeThirdPartyLinkActive : ''
                          }`}
                          onClick={() => setSelectedContact(activeContact.code)}
                          title={activeContact.description}
                        >
                          {activeContact.description.length > 20
                            ? `${activeContact.description.substring(0, 17)}...`
                            : activeContact.description}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              )}

              <tr>
                <th>Selected authorized third party:</th>
                <td>
                  <select
                    value={selectedContact}
                    onChange={(e) => {
                      setSelectedContact(e.target.value);
                    }}
                  >
                    <option value=''>Select option</option>
                    {contactsQID.map((c) => (
                      <option value={c.code} key={c.code}>
                        {c.code + ' - ' + c.description}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              {loanContacts && contact && (
                <tr>
                  <th>Active:</th>
                  <td>
                    <input
                      type='checkbox'
                      className={Styles.checkboxAnyTime}
                      checked={contact.active}
                      onChange={(e) => handleContactChange(contact.relationship, 'active', e.target.checked)}
                    ></input>
                  </td>
                </tr>
              )}

              {loanContacts && contact && contact.active && (
                <>
                  <tr>
                    <th>Name:</th>
                    <td>
                      <input
                        type='text'
                        value={contact.contactPersonFirstName || ''}
                        onChange={(e) =>
                          handleContactChange(contact.relationship, 'contactPersonFirstName', e.target.value)
                        }
                        placeholder='First Name'
                      />
                      <input
                        type='text'
                        value={contact.contactPersonLastName || ''}
                        onChange={(e) =>
                          handleContactChange(contact.relationship, 'contactPersonLastName', e.target.value)
                        }
                        placeholder='Last Name'
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Email:</th>
                    <td>
                      <input
                        type='email'
                        value={contact.contactPersonEmail || ''}
                        onChange={(e) =>
                          handleContactChange(contact.relationship, 'contactPersonEmail', e.target.value)
                        }
                        placeholder='Email'
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Phone:</th>
                    <td>
                      <input
                        type='text'
                        value={contact.contactPersonPhone || ''}
                        onChange={(e) =>
                          handleContactChange(contact.relationship, 'contactPersonPhone', e.target.value)
                        }
                        placeholder='Phone'
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Address:</th>
                    <td>
                      <input
                        type='text'
                        value={contact.address1 || ''}
                        onChange={(e) => handleContactChange(contact.relationship, 'address1', e.target.value)}
                        placeholder='Address'
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>City/State/Zip:</th>
                    <td>
                      <input
                        type='text'
                        value={contact.city || ''}
                        onChange={(e) => handleContactChange(contact.relationship, 'city', e.target.value)}
                        placeholder='City'
                      />
                      <input
                        type='text'
                        value={contact.companyState || ''}
                        onChange={(e) => handleContactChange(contact.relationship, 'companyState', e.target.value)}
                        placeholder='State'
                      />
                      <input
                        type='text'
                        value={contact.zip || ''}
                        onChange={(e) => handleContactChange(contact.relationship, 'zip', e.target.value)}
                        placeholder='Zip Code'
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Relationship:</th>
                    <td>{contact.relationship}</td>
                  </tr>
                  <tr>
                    <th>Auth Type:</th>
                    <td>
                      <select
                        value={contact.authType || ''}
                        onChange={(e) => handleContactChange(contact.relationship, 'authType', e.target.value)}
                      >
                        <option value=''>Select Auth Type</option>
                        <option value='I'>Information Only</option>
                        <option value='C'>Information and Can Make Changes and Payments</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <th>Effective Date:</th>
                    <td>
                      <input
                        type='date'
                        className={Styles.calicon}
                        value={
                          contact.authEffectiveDate
                            ? formatDateForInputWithOffset(Number(contact.authEffectiveDate))
                            : ''
                        }
                        onChange={(e) => {
                          const [year, month, day] = e.target.value.split('-');
                          const newAuthEffectiveDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          const timestamp = Math.floor(newAuthEffectiveDate.getTime() / 1000);
                          handleContactChange(contact.relationship, 'authEffectiveDate', timestamp);
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Expiration Date:</th>
                    <td>
                      <input
                        type='date'
                        className={Styles.calicon}
                        value={
                          contact.authExpirationDate
                            ? formatDateForInputWithOffset(Number(contact.authExpirationDate))
                            : ''
                        }
                        onChange={(e) => {
                          const [year, month, day] = e.target.value.split('-');
                          const newAuthExpirationDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          const timestamp = Math.floor(newAuthExpirationDate.getTime() / 1000);
                          handleContactChange(contact.relationship, 'authExpirationDate', timestamp);
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>PID:</th>
                    <td>
                      <input
                        type='text'
                        value={contact.authPIN || ''}
                        onChange={(e) => handleContactChange(contact.relationship, 'authPIN', e.target.value)}
                        placeholder='PIN'
                      />
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>

          <br></br>
        </div>
      </div>
    </EnvelopeWithBox>
  );
};

export default ThirdPartyInfo;
